import {
  OUTPOSTS,
  OUTPOST_RULES,
  PLANET_DEPOSITS,
  type OutpostKind,
  type TradeGoodId,
} from '@sd/content';
import type { ExplorationState } from './exploration';
import { hexDistance, type Hex } from './hex';
import type { StarSystem } from './system';

/**
 * Outposts and their sites (GDD §3, §5). A site is any neutral body: a
 * planet without a capital, a Lagrange point, or an asteroid revealed by a
 * scan. Pure helpers; the match drives them from `step`.
 */
export interface OutpostSite {
  /** Planet, Lagrange point or asteroid id. */
  readonly id: string;
  readonly hex: Hex;
  readonly kind: OutpostKind;
  /** What the station there produces. */
  readonly good: TradeGoodId;
}

export interface OutpostState {
  /** Same as its site's id: one outpost per site. */
  readonly id: string;
  readonly owner: string;
  readonly kind: OutpostKind;
  readonly hex: Hex;
  readonly good: TradeGoodId;
  /** Produces from the first cycle on or after this tick. */
  readonly readyTick: number;
  /** After a raid it produces nothing until this tick (GDD §8). */
  readonly downUntil?: number;
}

/** Every site of the map, in a stable order: planets, Lagrange points, asteroids. */
export function outpostSites(system: StarSystem, exploration: ExplorationState): OutpostSite[] {
  const sites: OutpostSite[] = [];
  for (const p of system.planets) {
    if (p.homeOf !== null) continue;
    if (p.kind === 'gasGiant') {
      sites.push({ id: p.id, hex: p.hex, kind: 'gasHarvester', good: fixedGood('gasHarvester') });
    } else {
      sites.push({ id: p.id, hex: p.hex, kind: 'miningStation', good: PLANET_DEPOSITS[p.kind] });
    }
  }
  for (const l of system.lagrangePoints) {
    sites.push({
      id: l.id,
      hex: l.hex,
      kind: 'lagrangeStation',
      good: fixedGood('lagrangeStation'),
    });
  }
  for (const a of exploration.asteroids) {
    sites.push({ id: a.id, hex: a.hex, kind: 'miningStation', good: 'metals' });
  }
  return sites;
}

/** The good of an outpost kind that does not take its body's deposit. */
function fixedGood(kind: OutpostKind): TradeGoodId {
  const good = OUTPOSTS[kind].good;
  if (good === 'deposit') throw new Error(`${kind} takes its body's deposit`);
  return good;
}

export const siteAt = (sites: readonly OutpostSite[], h: Hex): OutpostSite | undefined =>
  sites.find((s) => s.hex.q === h.q && s.hex.r === h.r);

/** Whether `h` lies in an outpost's zone of control. */
export const inZone = (outpost: OutpostState, h: Hex): boolean =>
  hexDistance(outpost.hex, h) <= OUTPOSTS[outpost.kind].zoneRadius;

/** Owner of the zone of control covering `h`, other than `except`; null if none. */
export function zoneOwner(
  outposts: readonly OutpostState[],
  h: Hex,
  except: string,
): string | null {
  return outposts.find((o) => o.owner !== except && inZone(o, h))?.owner ?? null;
}

function byLevel(level: number): number {
  const table = OUTPOST_RULES.limitBySpaceportLevel;
  const index = Math.min(table.length, Math.max(1, level)) - 1;
  return table[index] ?? 0;
}

/** Outposts a player may hold for its spaceport level (0 without a spaceport). */
export const outpostLimit = (spaceportLevel: number): number =>
  spaceportLevel > 0 ? byLevel(spaceportLevel) : 0;

/** Upkeep of a player's outposts for one cycle, ГС: each base × N^exponent. */
export function outpostUpkeep(owned: readonly OutpostState[]): number {
  const n = owned.length;
  if (n === 0) return 0;
  const scale = n ** OUTPOST_RULES.upkeepExponent;
  return owned.reduce((sum, o) => sum + OUTPOSTS[o.kind].upkeep * scale, 0);
}
